// tracePathAliases.js: one file addressed two ways.
//
// A Cowork session runs its shell inside a container and its file tools on the
// host, so the same directory has two names. The shell writes
// `/sessions/<container>/mnt/outputs/_qa/s-06.jpg` and the agent then reads
// `~/Library/.../local-agent-mode-sessions/<ids>/outputs/_qa/s-06.jpg`. Nothing
// in either path says they are the same place - the container cannot see its own
// mount point, and no command in the trace ever mentions the host path.
//
// The rail shows that as two files: one row saying "6 creates" and another
// saying "6 views", each telling half the story, with the writes and the reads
// of one image in different subtrees. A reader looking at the views can
// reasonably conclude something else is writing the file.
//
// Both rows STAY. What this module supplies is the relation between them, for
// the view to draw - see pathRelations for why collapsing them is the wrong
// trade.
//
// The rule is the one the backend's `resource_trie` already uses to find a shared
// project across endpoints, applied to roots within one trace. That trie
// enumerates every path SUFFIX from depth 2 to 8, scores a suffix by how many
// endpoints share it, and keeps the deepest suffix with identical support. Here
// the documents are ROOTS rather than endpoints:
//
//   1. Two paths that share a suffix of >= 2 segments propose a root pairing:
//      the prefixes left over on each side.
//   2. A pairing is accepted only when several DISTINCT suffixes support it.
//      One agreeing suffix is a coincidence - two checkouts both have
//      `src/index.js` - and that is what the trie's endpoint count guards
//      against.
//   3. The deepest agreeing suffix wins, so the roots are split as late as
//      possible and a shallow accident cannot merge two real trees.
//
// The >= 2 segment floor is what keeps `/tmp/build_deck.js` out of it: it shares
// only `build_deck.js` with the session copy, and /tmp is genuinely a different
// tree that the same command copies into.

// Matches the trie's `range(2, least(length(parts), 8) + 1)`: below 2 segments a
// suffix is a bare filename, and past 8 the extra depth buys nothing.
const MIN_SUFFIX_SEGMENTS = 2;
const MAX_SUFFIX_SEGMENTS = 8;
// How many distinct suffixes must agree before two roots are called one place.
// The trie spends an IDF score on this; one trace has too few paths for that to
// mean anything, so it is a floor instead.
const MIN_AGREEING_SUFFIXES = 3;
// Pairwise over the file paths of one trace: tens, not thousands. The cap is the
// backstop for a pathological trace, not a tuning knob.
const MAX_PATHS = 400;

const segmentsOf = (p) =>
  String(p || "")
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean);

/** Longest common suffix of two paths, in segments. */
function commonSuffix(a, b) {
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n += 1;
  return Math.min(n, MAX_SUFFIX_SEGMENTS);
}

const rootOf = (segs, suffixLen) => segs.slice(0, segs.length - suffixLen).join("/");

/**
 * Root pairings the paths themselves attest to.
 *
 * Returns `[{ roots: [a, b], suffixes: [...] }]`, each pairing supported by at
 * least MIN_AGREEING_SUFFIXES distinct suffixes. Roots are the leading segments
 * with no leading slash, matching how the trie stores a subpath.
 */
export function mountPairings(paths) {
  const list = [...new Set((paths || []).filter(Boolean))].slice(0, MAX_PATHS);
  const segs = list.map(segmentsOf);
  const pairs = new Map();
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const n = commonSuffix(segs[i], segs[j]);
      if (n < MIN_SUFFIX_SEGMENTS) continue;
      const rootA = rootOf(segs[i], n);
      const rootB = rootOf(segs[j], n);
      // Same root means one path, not two views of one.
      if (!rootA || !rootB || rootA === rootB) continue;
      // JSON rather than a joined string: a root can contain a space - the host
      // side of the pairing this was written for ends in `Application Support` -
      // and any single-character separator makes two different pairings collide.
      const key = JSON.stringify(rootA < rootB ? [rootA, rootB] : [rootB, rootA]);
      let entry = pairs.get(key);
      if (!entry) {
        entry = { roots: rootA < rootB ? [rootA, rootB] : [rootB, rootA], suffixes: new Set() };
        pairs.set(key, entry);
      }
      entry.suffixes.add(segs[i].slice(segs[i].length - n).join("/"));
    }
  }
  return [...pairs.values()]
    .filter((p) => p.suffixes.size >= MIN_AGREEING_SUFFIXES)
    .map((p) => ({ roots: p.roots, suffixes: [...p.suffixes].sort() }))
    .sort((a, b) => b.suffixes.length - a.suffixes.length || a.roots[0].localeCompare(b.roots[0]));
}

/**
 * Which of one trace's paths are the same file as which.
 *
 * ANNOTATION, not identity. Every path keeps its own row: the container spelling
 * is what the shell actually used and the host spelling is what the file tools
 * used, and rewriting either one onto the other would file a write under a path
 * the writer never saw. That the rail is a filesystem tree is the reason it can
 * be trusted, and a canonical-root rule also has to pick a winner - by op count,
 * which is not stable across traces.
 *
 * So this reports the relation and lets the view draw it:
 *   `siblingsOf(path)` - the other spellings of that same file in this trace
 *   `groupOf(path)`     - a stable key shared by every spelling of one file
 */
export function pathRelations(paths) {
  const pairings = mountPairings(paths);
  const list = [...new Set((paths || []).filter(Boolean))];
  const siblings = new Map();
  const groups = new Map();
  for (const { roots: [a, b] } of pairings) {
    for (const p of list) {
      const from = pathStartsWithRoot(p, a) ? a : pathStartsWithRoot(p, b) ? b : null;
      if (!from) continue;
      const to = from === a ? b : a;
      const suffix = segmentsOf(p).slice(segmentsOf(from).length).join("/");
      const twin = list.find((q) => q !== p && pathStartsWithRoot(q, to)
        && segmentsOf(q).slice(segmentsOf(to).length).join("/") === suffix);
      if (!twin) continue;
      if (!siblings.has(p)) siblings.set(p, new Set());
      siblings.get(p).add(twin);
      // Keyed on the shared suffix, so both spellings land in one group without
      // either root having to win.
      groups.set(p, `mount:${suffix}`);
    }
  }
  return {
    pairings,
    active: siblings.size > 0,
    siblingsOf: (path) => [...(siblings.get(path) || [])].sort(),
    groupOf: (path) => groups.get(path) || null,
  };
}

function pathStartsWithRoot(path, root) {
  const p = segmentsOf(path);
  const r = segmentsOf(root);
  if (r.length === 0 || p.length <= r.length) return false;
  return r.every((seg, i) => p[i] === seg);
}
